import { useTranslation } from "react-i18next";
import { useListEventsQuery } from "@/state/eventsApi";
import type { PastEvent } from "@/types/events";

export default function PastEvents() {
  const { t } = useTranslation();
  const { data, isLoading } = useListEventsQuery({ type: 'past' });
  const events = (data?.data ?? []) as PastEvent[];

  return (
    <section className="py-24 bg-[#f8fafc]">
      <div className="w-full px-6 lg:px-12">
        <div className="mb-12">
          <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-3">{t("pastTag")}</p>
          <h2 className="text-4xl font-bold text-[#0A1628] leading-tight">
            {t("pastTitle1")} <span className="text-[#00C2C2]">{t("pastTitle2")}</span>
          </h2>
          <p className="text-gray-500 text-base mt-4 max-w-xl">{t("pastDesc")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-lg border border-gray-100 overflow-hidden">
                  <div className="w-full h-44 bg-gray-100" />
                  <div className="p-5 flex flex-col gap-3">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                    <div className="h-3 bg-gray-100 rounded w-5/6" />
                    <div className="h-3 bg-gray-100 rounded w-1/2 mt-2" />
                  </div>
                </div>
              ))
            : events.map((event) => (
                <div key={event.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden group">
                  <div className="w-full h-44 overflow-hidden relative">
                    {event.image
                      ? <img src={event.image} alt={event.title} loading="lazy" className="w-full h-full object-cover object-top opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                      : <div className="w-full h-full bg-[#0A1628]/20" />
                    }
                    <div className="absolute inset-0 bg-[#0A1628]/30" />
                    <div className="absolute bottom-3 left-3">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white backdrop-blur-sm">
                        {event.format === "Online" ? <><i className="ri-global-line mr-1" />{t("upcomingOnline")}</> : <><i className="ri-map-pin-2-line mr-1" />{t("upcomingInPerson")}</>}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{event.type}</span>
                    </div>
                    <h3 className="text-sm font-bold text-[#0A1628] leading-snug mb-2">{event.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{event.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-100">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line text-[#00C2C2]" />
                        {new Date(event.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      <span className="flex items-center gap-1 font-semibold text-[#0A1628]">
                        <i className="ri-group-line text-[#00C2C2]" />
                        {event.attendees.toLocaleString()} {t("pastAttendees")}
                      </span>
                    </div>
                  </div>
                </div>
              ))
          }

          {!isLoading && events.length === 0 && (
            <div className="col-span-3 text-center py-16 text-gray-400">
              <i className="ri-history-line text-3xl mb-3 block" />
              <p className="font-semibold text-[#0A1628]">No past events yet</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
